<template>
    <div>
        <header>
            <v-container>
                <v-row no-gutters>
                    <h1 class="font-weight-light headline">
                        {{ $t("title.licences_web") }}
                    </h1>
                    <v-spacer></v-spacer>
                    <v-lazy
                        v-if="isLicencesLoaded"
                        transition="fade-transition"
                    >
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="searchLabel"
                            single-line
                            dense
                            hide-details
                            style="max-width: 200px"
                            class="mr-5 pr-5"
                        ></v-text-field>
                    </v-lazy>
                    <v-lazy transition="fade-transition">
                        <v-btn
                            color="success"
                            dark
                            small
                            @click.stop="
                                dialog.licenceForm = true;
                                isLicenceEdited = false;
                                selectedLicence = { ...defaultLicence };
                            "
                        >
                            {{ $t("general.add") }}
                        </v-btn>
                    </v-lazy>
                </v-row>
            </v-container>
        </header>

        <section class="py-7">
            <v-container>
                <v-sheet class="px-3 pt-3 pb-3" v-if="!isLicencesLoaded">
                    <v-skeleton-loader
                        class="mx-auto"
                        type="table"
                    ></v-skeleton-loader>
                </v-sheet>

                <v-card v-else>
                    <v-data-table
                        :headers="headers"
                        :items="licences"
                        :search="search"
                    >
                        <template v-slot:item.action="{ item }">
                            <div style="display: flex">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            v-tr-class-on-hover="'blue'"
                                            size="20px"
                                            color="blue"
                                            class="mr-2"
                                            @click="editLicence(item)"
                                            v-on="on"
                                        >
                                            mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.edit") }}</span>
                                </v-tooltip>

                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            size="22px"
                                            color="red"
                                            v-tr-class-on-hover="'red'"
                                            class="mr-2"
                                            @click="confirmDelete(item)"
                                            v-on="on"
                                        >
                                            mdi-delete-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.delete") }}</span>
                                </v-tooltip>
                            </div>
                        </template>

                        <template v-slot:item.title="{ item }">
                            <div class="text-truncate" style="max-width: 350px">
                                {{ item.title }}
                            </div>
                        </template>

                        <template
                            v-for="language in languages"
                            v-slot:[`item.title_${language.code}`]="{ item }"
                        >
                            <div
                                :key="`licence-title-${language.code}-${item.id}`"
                                class="text-truncate"
                                style="max-width: 300px"
                            >
                                {{ item[`title_${language.code}`] || "-" }}
                            </div>
                        </template>

                        <template v-slot:item.is_active="{ item }">
                            <v-switch
                                class="mt-0 pt-0 justify-content-center"
                                hide-details
                                color="green"
                                v-model="item.is_active"
                                @change="changeStatus(item)"
                            ></v-switch>
                        </template>
                    </v-data-table>
                </v-card>

                <LicencesWebForm
                    :licence="selectedLicence"
                    :languages="languages"
                    :visible="dialog.licenceForm"
                    :isEdited="isLicenceEdited"
                    @addLicence="addLicence"
                    @updateLicence="updateLicence"
                    @close="dialog.licenceForm = false"
                    @snackMessage="snackMessage"
                />

                <ConfirmationDialog
                    :name="selectedLicence.title"
                    :text="'licence_web.delete_confirmation'"
                    v-if="dialog.confirmation"
                    @confirm="deleteLicence"
                    @cancel="cancelDelete"
                />

                <SnackMessage ref="SnackMessage" />
            </v-container>
        </section>
    </div>
</template>

<script>
import LicencesWebForm from "../components/LicencesWebForm";
import ConfirmationDialog from "../components/ConfirmationDialog";
import SnackMessage from "../components/SnackMessage";

export default {
    data() {
        return {
            languages: [],
            licences: [],
            isLicencesLoaded: false,
            search: "",
            dialog: {
                confirmation: false,
                licenceForm: false,
            },
            selectedLicence: {
                id: null,
                title: "",
                subtitle: "",
                description: "",
                icon_tag: "",
                is_popular: 0,
                is_active: 0,
            },
            defaultLicence: {
                id: null,
                title: "",
                subtitle: "",
                description: "",
                icon_tag: "",
                is_popular: 0,
                is_active: 0,
            },
            isLicenceEdited: false,
        };
    },

    components: {
        LicencesWebForm,
        ConfirmationDialog,
        SnackMessage,
    },

    computed: {
        headers() {
            const baseHeaders = [
                {
                    text: this.$t("general.actions"),
                    value: "action",
                    sortable: false,
                    width: "100px",
                },
                {
                    text: this.$t("licence_web.title_cz"),
                    align: "left",
                    sortable: true,
                    value: "title",
                },
            ];

            const languageHeaders = this.languages.map((lang) => ({
                text:
                    this.$t("licence_web.title_lang") +
                    " " +
                    lang.name +
                    " (" +
                    lang.code +
                    ")",
                align: "left",
                sortable: false,
                value: `title_${lang.code}`,
            }));

            const activeHeader = {
                text: this.$t("general.active"),
                align: "center",
                sortable: false,
                value: "is_active",
                width: "100px",
            };

            return [...baseHeaders, ...languageHeaders, activeHeader];
        },
        searchLabel() {
            return this.$t("general.search");
        },
    },

    created() {
        this.getLicences();
    },

    watch: {
        languages(newLanguages) {
            const base = {
                id: null,
                title: "",
                subtitle: "",
                description: "",
                icon_tag: "",
                is_popular: 0,
                is_active: 0,
            };

            newLanguages.forEach((lang) => {
                base[`title_${lang.code}`] = "";
                base[`subtitle_${lang.code}`] = "";
                base[`description_${lang.code}`] = "";
            });

            this.defaultLicence = base;

            if (!this.selectedLicence.id) {
                this.selectedLicence = { ...base };
            }
        },
    },

    methods: {
        snackMessage(msg, type) {
            this.$refs.SnackMessage.showMessage(msg, type);
        },

        async getLicences() {
            try {
                const response = await axios.get("/licences-web", {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                const { languages, licences } = response.data;

                this.languages = (languages || []).filter(
                    (lang) => lang.code !== "cz"
                );

                this.licences = licences || [];
                this.isLicencesLoaded = true;
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        addLicence(licence) {
            this.licences.push(licence);
        },

        editLicence(item) {
            this.selectedLicence = { ...item };
            this.dialog.licenceForm = true;
            this.isLicenceEdited = true;
        },

        updateLicence(licence) {
            const index = this.licences.findIndex(
                (item) => item.id == licence.id
            );
            if (index !== -1) {
                this.licences.splice(index, 1, licence);
            }
        },

        confirmDelete(item) {
            this.selectedLicence = item;
            this.dialog.confirmation = true;
        },

        cancelDelete() {
            this.dialog.confirmation = false;
        },

        async deleteLicence() {
            this.dialog.confirmation = false;
            const index = this.licences.indexOf(this.selectedLicence);

            try {
                await axios.delete(`/licences-web/${this.selectedLicence.id}`, {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                if (index !== -1) {
                    this.licences.splice(index, 1);
                }
                this.selectedLicence = { ...this.defaultLicence };
                this.snackMessage(
                    "licence_web.deleted_successfully",
                    "success"
                );
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        async changeStatus(item) {
            try {
                await axios.put(
                    `/licences-web/status/${item.id}`,
                    { is_active: item.is_active },
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                        },
                    }
                );
                this.snackMessage("general.item_saved", "success");
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },
    },
};
</script>
